import { isSealedScan, type ScanReviewGroup } from "@hobbyshopify/backend-client";
import { describeCandidate, selectedProductId } from "../core/primitives/candidates";
import { scanImage } from "../core/primitives/card-display";
import { hasCandidateChoice, needsCandidatePick } from "../core/primitives/review-row";
import { CountStepper } from "./CountStepper";
import { EbayQuickLinks } from "./EbayQuickLinks";
import { reviewGradeKey, reviewIdentity, reviewNotes, reviewTitle } from "./review-group-row";
import { CheckIcon, CrossIcon } from "./review-icons";

/**
 * One row of the review queue, built for confirming at speed: the reading, the
 * look-alikes to pick between, the comps, a price, a count and a check. The rest
 * is one tap away under Compare or Details. A port of the iOS
 * `AdminReviewQuickRow`, and the same row on Inventory › Review and on a
 * customer's page.
 *
 * A row is every pending scan that read as the same card: ten copies are one
 * row, one confirm and one lot of ten.
 */
export function ReviewGroupRow({
  group,
  busy,
  price,
  quantity,
  showQuantity,
  canConfirm,
  pickingProductId,
  approveCodes = false,
  catalogDisputed = false,
  onApproveCodes,
  onPrice,
  onQuantity,
  onPick,
  onConfirm,
  onReview,
  onReject,
  onOpenImage,
}: {
  group: ScanReviewGroup;
  busy?: boolean;
  price: string;
  quantity: number;
  /** False for a customer's scan: a price question, not stock coming in. */
  showQuantity: boolean;
  canConfirm: boolean;
  /** The candidate a pick is running for on this row. */
  pickingProductId: string | null;
  /** The row's codes are approved as the shop's vote in the shared catalogue. */
  approveCodes?: boolean;
  /** Shops disagree about the code; only the editor settles it. */
  catalogDisputed?: boolean;
  /** Without it the row offers no tick, and confirms with the codes the shop's own device read. */
  onApproveCodes?: (approve: boolean) => void;
  onPrice: (price: string) => void;
  onQuantity: (quantity: number) => void;
  onPick: (productId: string) => void;
  onConfirm: () => void;
  /** The full review screen: Compare when there are look-alikes, Details otherwise. */
  onReview: () => void;
  onReject: () => void;
  onOpenImage: () => void;
}) {
  const scan = group.sample;
  const exists = group.bucket === "exists";
  const unvetted = group.bucket === "unvetted";
  const title = reviewTitle(group);
  const identity = reviewIdentity(group);
  const notes = reviewNotes(group);
  const picked = selectedProductId(scan.scpData);
  const mustPick = needsCandidatePick(scan);
  const candidates = hasCandidateChoice(scan) ? scan.candidates : [];
  const image = scanImage(scan);
  const priceId = `review-price-${group.key}`;
  const sealed = scan.itemKind === "sealed";
  const confirmLabel = canConfirm
    ? group.count > 1
      ? `Confirm all ${group.count}: ${title}`
      : `Confirm ${title}`
    : catalogDisputed
      ? "Review to settle the barcode"
      : mustPick
        ? "Pick a match to confirm"
        : "Type a price to confirm";

  return (
    <div className={`rounded-row border border-line bg-surface p-3 transition-opacity ${busy ? "opacity-60" : ""}`}>
      <div className="flex items-start gap-3">
        <button
          type="button"
          onClick={onOpenImage}
          disabled={!image}
          aria-label={`Photo of ${title}`}
          className="relative h-16 w-[46px] shrink-0 overflow-hidden rounded-thumb bg-surface-strong"
        >
          {image ? (
            <img src={image} alt="" className="h-full w-full object-cover" />
          ) : (
            <span className="flex h-full w-full items-center justify-center text-[10px] text-muted">No img</span>
          )}
          {group.count > 1 && (
            // On the photograph, because it counts the pile of scans: ten scans, one card.
            <span className="absolute right-0 bottom-0 rounded-tl bg-app/80 px-1 text-[10px] font-semibold text-white">
              ×{group.count}
            </span>
          )}
        </button>

        <button type="button" onClick={onReview} className="min-w-0 flex-1 text-left">
          <span className="block truncate text-[15px] font-semibold text-white">{title}</span>
          <span className="mt-1 flex min-w-0 items-center gap-1.5">
            {/* Tinted, as iOS draws its badges: the solid accent is for what is confirmed, and this is not yet. */}
            <span
              className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                exists ? "bg-accent/15 text-accent" : "bg-warn/15 text-warn"
              }`}
            >
              {exists ? "Matches" : unvetted ? "Unvetted" : sealed ? "New product" : "New card"}
            </span>
            {identity && <span className="truncate text-xs text-muted">{identity}</span>}
          </span>
          {notes.map((note) => (
            <span key={note} className="mt-1 block text-xs text-warn">
              {note}
            </span>
          ))}
        </button>

        <button
          type="button"
          onClick={onReject}
          disabled={busy}
          aria-label="Reject"
          title="Reject"
          className="-mt-1.5 -mr-1.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-btn text-faint transition-colors hover:text-white disabled:opacity-40"
        >
          <CrossIcon />
        </button>
      </div>

      {catalogDisputed ? (
        <p className="mt-2 text-xs text-warn">Shops disagree about this barcode. Review it to pick the product.</p>
      ) : (
        onApproveCodes &&
        scan.suggestedCodes.length > 0 && (
          <label className="mt-2 flex items-center gap-2 text-xs text-muted">
            <input
              type="checkbox"
              className="accent-accent"
              checked={approveCodes}
              disabled={busy}
              onChange={(e) => onApproveCodes(e.target.checked)}
            />
            <span className="min-w-0">
              Save {scan.suggestedCodes.map((c) => c.value).join(", ")} as this {sealed ? "product" : "card"} in the
              shared catalogue
            </span>
          </label>
        )
      )}

      {candidates.length > 0 ? (
        <div className="mt-2.5">
          <div className="flex items-baseline justify-between gap-2">
            <p className={`eyebrow ${mustPick ? "text-warn" : "text-muted"}`}>
              {mustPick ? "Pick the right match" : `${candidates.length} possible matches`}
            </p>
            <button type="button" onClick={onReview} className="shrink-0 text-xs font-semibold text-accent">
              Compare ›
            </button>
          </div>
          {/* Scrolled sideways, as on iOS; a thin bar where the pointer needs one, none on a phone. */}
          <ul className="mt-1.5 flex gap-1.5 overflow-x-auto pb-1 [scrollbar-width:thin]">
            {candidates.map((c) => {
              const line = describeCandidate(c.product);
              const current = line.productId !== null && line.productId === picked;
              const picking = pickingProductId !== null && pickingProductId === line.productId;
              return (
                <li key={`${c.rank}-${line.productId ?? line.title}`} className="shrink-0">
                  <button
                    type="button"
                    disabled={busy || line.productId === null || pickingProductId !== null}
                    onClick={() => !current && line.productId && onPick(line.productId)}
                    aria-pressed={current}
                    aria-label={`Identify as ${line.title}${line.subtitle ? `, ${line.subtitle}` : ""}`}
                    className={`block max-w-[190px] rounded-btn border px-2.5 py-1.5 text-left transition-colors disabled:cursor-default ${
                      current
                        ? "border-accent bg-accent/15"
                        : mustPick
                          ? "border-warn/55 hover:border-warn"
                          : "border-line hover:border-accent"
                    }`}
                  >
                    <span
                      className={`block truncate text-[13px] font-semibold ${current ? "text-accent" : "text-white"}`}
                    >
                      {line.label}
                    </span>
                    <span className="block truncate text-[11px] text-muted">
                      {picking ? (
                        "Switching…"
                      ) : (
                        <>
                          {c.confidence !== null && `${c.confidence}% · `}
                          <span className={c.matchedCardId ? "text-accent" : ""}>
                            {c.matchedCardId ? "In stock" : "New"}
                          </span>
                        </>
                      )}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ) : (
        <div className="mt-1 flex justify-end">
          <button type="button" onClick={onReview} className="text-xs font-semibold text-accent">
            Details ›
          </button>
        </div>
      )}

      {/* Sold prices by grade are a card's question; a box is priced from its market estimate. */}
      {!isSealedScan(scan) && (
        <div className="mt-2.5">
          <EbayQuickLinks
            title={title}
            ownKey={reviewGradeKey(group)}
            pricedKey={group.card?.currentPriceCents == null ? scan.estimatedPriceGrade : null}
            searchLinks={scan.searchLinks}
            scpData={scan.scpData}
          />
        </div>
      )}

      <div className="mt-2.5 flex items-center gap-2">
        <label
          htmlFor={priceId}
          className={`flex h-10 w-28 shrink-0 items-center gap-1 rounded-btn border bg-surface px-3 text-[15px] font-semibold focus-within:border-accent ${
            canConfirm || mustPick ? "border-line" : "border-warn"
          }`}
        >
          <span className="text-muted">$</span>
          <input
            id={priceId}
            inputMode="decimal"
            aria-label="Price"
            placeholder="Price"
            className="w-full min-w-0 bg-transparent text-white outline-none placeholder:font-normal placeholder:text-muted"
            value={price}
            onChange={(e) => onPrice(e.target.value)}
            disabled={busy}
          />
        </label>

        {/* Below one is untracked: confirmed without a count, not zero copies. */}
        {showQuantity && <CountStepper count={quantity} onChange={onQuantity} zeroLabel="Untracked" disabled={busy} />}

        <span className="flex-1" />

        <button
          type="button"
          onClick={onConfirm}
          disabled={busy || !canConfirm}
          aria-label={confirmLabel}
          title={confirmLabel}
          className={`flex h-11 w-12 shrink-0 items-center justify-center rounded-btn transition-colors ${
            canConfirm ? "bg-accent text-ink" : "bg-surface-strong text-faint"
          }`}
        >
          <CheckIcon />
        </button>
      </div>
    </div>
  );
}

/**
 * How much is waiting, and the one tap that confirms every row already priced.
 * A row still missing a price or a pick stays behind. Without `onConfirmAll`
 * the page offers no batch at all.
 */
export function ReviewSummary({
  detail,
  pricedCount,
  confirming,
  disabled,
  onConfirmAll,
}: {
  detail: string;
  pricedCount: number;
  confirming?: boolean;
  disabled?: boolean;
  onConfirmAll?: () => void;
}) {
  return (
    <div className="flex items-center gap-2.5 pt-1">
      <div className="min-w-0 flex-1">
        <p className="eyebrow text-muted">Needs review</p>
        <p className="text-xs text-muted">{detail}</p>
      </div>
      {onConfirmAll && pricedCount > 0 && (
        <button
          type="button"
          onClick={onConfirmAll}
          disabled={disabled}
          className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-btn border border-accent/55 px-3.5 text-sm font-semibold text-accent transition-opacity disabled:opacity-50"
        >
          <CheckIcon size={15} />
          {confirming ? "Confirming…" : `Confirm all priced (${pricedCount})`}
        </button>
      )}
    </div>
  );
}
